import React from 'react';
import { Modal, Collapse } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { checkIntegrationCode } from 'services/settings/integrationCode';

const { Panel } = Collapse;

/**
 * Handles the integration code validation workflow:
 * 1. Checks for duplicate integration codes via API.
 * 2. If duplicates found, shows a confirmation modal.
 * 3. If NO duplicates, calls onConfirm immediately.
 *
 * @param {string} code - Integration code to check
 * @param {object} options - { currentItemId, onConfirm, onCancel }
 *                             currentItemId: ID of the item being edited (to exclude from duplicates)
 */
export async function showIntegrationCodeModal(code, {
	currentItemId,
	onConfirm,
	onCancel
}) {
	// If code is empty, no need to check
	if (!code || code.trim() === '') {
		onConfirm();
		return;
	}

	const matches = await checkIntegrationCode(code);

	// Filter out the current item if we're in update mode
	const duplicates = (matches || []).filter(item => {
		// Try to find the ID in common fields
		const itemId = item.itemid || item.examItemID || item.examitemid || item.id || item.ID;
		return String(itemId) !== String(currentItemId);
	});

	if (duplicates.length > 0) {
		// Group duplicates by modulename
		const groupedDeps = (duplicates || []).reduce((acc, dep) => {
			const module = dep.modulename || 'Others';
			if (!acc[module]) acc[module] = [];
			acc[module].push({
				name: dep.itemname || dep.examItemName || dep.examitemname || 'Unknown Item',
				section: dep.sectionname,
				specimen: dep.specimenname
			});
			return acc;
		}, {});

		Modal.warning({
			title: 'Integration Code Already in Use',
			icon: <ExclamationCircleOutlined />,
			content: (
				<div style={{ marginTop: 20 }}>
					<p> The integration code <strong>"{code}"</strong> is already assigned to the following items:</p>
					
					<Collapse accordion ghost defaultActiveKey={Object.keys(groupedDeps)[0]}>
						{Object.entries(groupedDeps).map(([module, items], index) => (
							<Panel 
								header={<span><strong>{module}</strong> ({items.length})</span>} 
								key={module}
							>
								<ul style={{ margin: 0, paddingLeft: '20px', maxHeight: '150px', overflowY: 'auto' }}>
									{items.map((item, i) => (
										<li key={i}>
											{item.name}
											{item.section && item.specimen && item.section !== 'N/A' && (
												<span style={{ color: '#8c8c8c', fontSize: '12px', marginLeft: '5px' }}>
													({item.section} - {item.specimen})
												</span>
											)}
										</li>
									))}
								</ul>
							</Panel>
						))}
					</Collapse>

					<p style={{ marginTop: 15, fontWeight: 'bold', color: '#ff4d4f' }}>
						Duplicate Integration Codes are not allowed across the entire system
					</p>
				</div>
			),
			centered: true,
			width: 500,
			okText: 'Close',
			onOk: onCancel,
		});
	} else {
		// If no duplicates (or only itself), proceed normally
		onConfirm();
	}
}

export default showIntegrationCodeModal;
